import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import Title from '../components/Title';
import Table from '../components/Table';
import Avatar from '../components/Avatar';
import { SearchInput } from  '../components/SearchInput'

import useDebounce from '../hooks/useDebounce';
import useQueryParams from '../hooks/useQuery';
import { StatusText, ParticipantAvatarList } from './ContractsTable';

import { useIncomingContractList } from "../api/hooks/contracts";
import { fetcher } from "../api/client";
import { useTranslation } from "react-i18next";
import { downloadFile } from "../utils";

function IncomingContractsTableContainer({ className }) {
  const { templateId } = useParams();
  const query = useQueryParams()
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState(query.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);
  const params = useMemo(() => ({
    page: currentPage,
    perPage: 8,
    search: debouncedSearch,
    templateId
  }), [currentPage, debouncedSearch, templateId])

  const { isFetching, isLoading, data = {}, error } = useIncomingContractList(params);

  const history = useHistory();
  const { t } = useTranslation('common');

  const goBack = () => {
      history.goBack()
  }

  const handleSearch = (evt) => {
    const value = evt?.target?.value ?? '';
    setCurrentPage(1);
    setSearch(value)
    history.replace(`/inbox?search=${value}`)
  }

  const handleNextPage = () => {
    const newPage = Math.min(currentPage + 1, data.numPages);
    setCurrentPage(newPage)
  }

  const handlePrevPage = () => {
    const newPage = Math.max(currentPage - 1, 1);
    setCurrentPage(newPage)
  }

  const handleDownloadDocument = (document) => {
    return downloadFile({ name: document.name, url: document.importedPdf })
  }


  return (
      <div className={className}>  
          <Title 
            className="mb-4"
          >
              <div className="d-flex justify-content-end">
                <div>
                  <SearchInput 
                      placeholder={ t('forms.search')}
                      className={`form-control align-self-center `}
                      value={search}
                      onChange={handleSearch}
                  /> 
                </div>
              </div>
          </Title>
          <Table 
            paginationData={data}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            isLoading={isLoading || isFetching}
            hasError={error}
            EmptyTableProps={{
              primary: t('noDocuments'),
              secondary: t('noResults')
            }}
            columns={[
              {
                header: 'ID',
                key: 'documentId',
              },
              {
                header: t('document'),
                key: 'name',
                className: 'text-black-50',
              },
              {
                header: t('status'),
                key: 'status',
                render: (contract) => { 
                  return <StatusText contract={contract} />
                }
              },
              {
                header: t('shieldType'),
                key: 'shield',
                className: 'text-center',
                render: (contract) => {
                  return <span className="p-2 rounded bg-grey">{t('shield', { context: 'short', number: contract.shield })}</span>
                }
              },
              {
                header:  t('participant', {count: 2}),
                key: 'participants',
                className: 'text-center',
                render:(contract) => {
                    return(
                          <ParticipantAvatarList contract={contract}  />
                    )
                },
              }
            ]}
            data={data.data}
            actions={[
              {
                key: 'importedPdf',
                icon: 'cloud-download-alt',
                isLink: false,
                onClick: handleDownloadDocument,
                shouldShow: (doc) => !!doc.importedPdf,
                label: `${t('actions.download')} ${t('document')}`
              },
              {
                key: 'url',
                icon: 'external-link-alt',
                isLink: true,
                shouldShow: (doc) => !!doc.url,
                label: t('actions.viewDocument')
              }
            ]}
          />
          
      </div>
  ) 
}

export default IncomingContractsTableContainer;

